import { z } from 'zod';
import { DomainError } from '../errors.js';
import { addMonths, compareLocalDate, parseLocalDate } from '../local-date.js';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import {
  RA_ASSESSMENT_TYPES,
  RA_CONTROL_HIERARCHY,
  RA_CONTROL_REQUIREMENTS,
  RA_VESSEL_GROUPS,
  type RaActivityData,
  type RaControlHierarchy,
  type RaLinkData,
  type RaRowControlStatus,
  type RaShipGroup,
} from './master.js';
import {
  bandOf,
  type RaAcceptance,
  type RaAcceptor,
  type RaMatrixDefinition,
  type RaMatrixSnapshot,
  type RiskRating,
  ratingIn,
} from './matrix.js';
import type {
  ControlType,
  RiskAssessmentContent,
  RiskControl,
  RiskHazard,
  RiskTemplateContent,
} from './risk.js';
import { MAX_REVIEW_MONTHS } from './risk.js';

/**
 * Şirket risk değerlendirmesi kütüphanesi (K3; ADR 0087). Şirket RA'yı Builder'da yazar, onaya
 * gönderir, gözden geçirir ve yayımlar; yayım değiştirilemez bir sürüm üretir. Gemi yayımlı sürümü
 * değiştiremez; kendi işe özel değerlendirmesini ("Vessel Task-Specific RA") yayımlı sürümden
 * başlatır (sahip kararı 2026-10-19, soru 8).
 *
 * Her satır adım + tehlike + sonuç senaryosudur (FD §5 Tab 2). Satır üç puan taşır (R7):
 * - **inherent**: ilk risk (Tab 3; L ve S seçilir, skor elle değişmez),
 * - **current**: isteğe bağlı; yalnız yerinde olduğu doğrulanmış mevcut kontrollerle düşer
 *   ("seçmek uygulandı demek değildir", Tab 4),
 * - **target residual**: ek önlemlerden sonra beklenen risk (Tab 6). Taslak önlem riski düşürmez;
 *   bütün ek önlemler doğrulanana dek hedef bir tahmindir (Tab 5; XL W-01, W-02).
 *
 * Kabul durumu matris sürümünün bandından türer (ADR 0085 §2): Acceptable / Requires Additional
 * Controls / Escalation Required / Unacceptable. Modül saftır: matris ve bugünün günü parametredir.
 */

/** İş akışı durumları (FD §5 Tab 7, §12; SS BR-06). */
export const RA_LIBRARY_STATUSES = [
  'draft',
  'submitted',
  'under_review',
  'returned',
  'approved',
  'published',
] as const;
export type RaLibraryStatus = (typeof RA_LIBRARY_STATUSES)[number];

/** Değiştirilemez olay izinin olayları (`ra_assessment_events.kind`). */
export const RA_LIBRARY_EVENTS = [
  'submitted',
  'review_started',
  'returned',
  'accepted',
  'approved',
  'published',
] as const;
export type RaLibraryEvent = (typeof RA_LIBRARY_EVENTS)[number];

/** Mevcut kontrol yerinde mi (Tab 4): seçmek uygulandı demek değildir; varsayılan bilinmiyor. */
export const RA_IN_PLACE = ['yes', 'no', 'unknown'] as const;
export type RaInPlace = (typeof RA_IN_PLACE)[number];

/** Ek önlemin beklenen etkisi (Tab 5). */
export const RA_EXPECTED_EFFECTS = ['likelihood', 'severity', 'both'] as const;
export type RaExpectedEffect = (typeof RA_EXPECTED_EFFECTS)[number];

/** Ek önlemin durumu (Tab 5): `draft` öneridir ve riski düşürmez; `verified` doğrulanmıştır. */
export const RA_ADDITIONAL_STATUSES = [
  'draft',
  'planned',
  'in_progress',
  'implemented',
  'verified',
] as const;
export type RaAdditionalStatus = (typeof RA_ADDITIONAL_STATUSES)[number];

/** Kabul eden rollerin ofiste olanları: yayımdan önce kabul eder (K3). Kaptan gemide (K5). */
export const RA_OFFICE_ACCEPTORS: readonly RaAcceptor[] = ['superintendent', 'dpa'];

export const RA_LIBRARY_MAX_ROWS = 100;
export const RA_LIBRARY_MAX_CONTROLS = 20;
/** Değerlendirme kimliği: `RA-` + faaliyet ya da şablon kodu. */
export const RA_ASSESSMENT_CODE_MAX = 44;

const ID = /^[A-Za-z0-9-]{1,40}$/;
const ASSESSMENT_CODE = /^RA-[A-Z0-9][A-Z0-9._-]{0,40}$/;
const REVISION = /^(\d{1,3})\.(\d{1,3})$/;

const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const text = (max: number) =>
  z
    .string({ error: 'REQUIRED' })
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
/** Boş metin `null` olur. */
const optionalText = (max: number) =>
  z
    .string({ error: 'INVALID' })
    .transform((v) => v.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((v) => (v === '' ? null : v))
    .nullish()
    .transform((v) => v ?? null);
const nullable = <T extends z.ZodType>(schema: T) =>
  schema.nullish().transform((v) => (v ?? null) as z.output<T> | null);

function isLocalDate(value: string): boolean {
  try {
    parseLocalDate(value);
    return true;
  } catch {
    return false;
  }
}
const localDate = z.string({ error: 'INVALID' }).refine(isLocalDate, 'INVALID_DATE');
const scale = z
  .number({ error: 'REQUIRED' })
  .int('INVALID')
  .min(1, 'OUT_OF_RANGE')
  .max(5, 'OUT_OF_RANGE');
const id = z.string({ error: 'REQUIRED' }).regex(ID, 'INVALID');
const uuid = z.uuid({ error: 'INVALID' });

function uniqueList<T extends z.ZodType>(item: T, min: number, max: number) {
  return z
    .array(item)
    .min(min, 'TOO_FEW')
    .max(max, 'TOO_MANY')
    .refine((list) => new Set(list).size === list.length, 'DUPLICATE');
}

const hierarchy = uniqueList(z.enum(RA_CONTROL_HIERARCHY, { error: 'INVALID' }), 0, 5);

const existingControlSchema = z.strictObject({
  id,
  /** Kütüphane kontrolü (`ra_controls`); serbest yazılmış önlemde `null`. */
  controlId: nullable(uuid),
  measure: text(500),
  hierarchy,
  requirement: nullable(z.enum(RA_CONTROL_REQUIREMENTS, { error: 'INVALID' })),
  procedure: optionalText(300),
  owner: optionalText(120),
  evidence: optionalText(500),
  inPlace: z.enum(RA_IN_PLACE, { error: 'INVALID' }),
  effectiveness: optionalText(500),
});
export type RaExistingControl = z.output<typeof existingControlSchema>;

const additionalControlSchema = z.strictObject({
  id,
  controlId: nullable(uuid),
  measure: text(500),
  hierarchy,
  expectedEffect: nullable(z.enum(RA_EXPECTED_EFFECTS, { error: 'INVALID' })),
  effectRationale: optionalText(500),
  responsibleRole: optionalText(120),
  dueDay: nullable(localDate),
  evidence: optionalText(500),
  verifier: optionalText(120),
  completionCriteria: optionalText(500),
  status: z.enum(RA_ADDITIONAL_STATUSES, { error: 'INVALID' }),
});
export type RaAdditionalControl = z.output<typeof additionalControlSchema>;

const ratingSchema = z.strictObject({ likelihood: scale, severity: scale });

const rowSchema = z.strictObject({
  id,
  /** Kaynak faaliyet–tehlike bağı (`ra_activity_hazards`); serbest satırda `null`. */
  linkId: nullable(uuid),
  stepId: nullable(z.string({ error: 'INVALID' }).regex(ID, 'INVALID')),
  step: text(300),
  /** Ana tehlike (`ra_tehlike`); şablondan dönüşen satırda serbest metin, `null`. */
  hazardId: nullable(uuid),
  hazard: text(300),
  /** "Request new hazard": şirket ana tehlikeyi ekleyip bağlayana dek yayımlanmaz. */
  hazardRequested: z.boolean({ error: 'INVALID' }),
  consequenceIds: uniqueList(uuid, 0, 50),
  consequence: optionalText(1000),
  exposed: optionalText(300),
  initial: ratingSchema.extend({
    rationale: optionalText(1000),
    assumptions: optionalText(1000),
  }),
  current: nullable(ratingSchema),
  existingControls: z.array(existingControlSchema).max(RA_LIBRARY_MAX_CONTROLS, 'TOO_MANY'),
  additionalControls: z.array(additionalControlSchema).max(RA_LIBRARY_MAX_CONTROLS, 'TOO_MANY'),
  residual: nullable(
    ratingSchema.extend({ rationale: optionalText(1000), dependency: optionalText(1000) }),
  ),
});
export type RaLibraryRow = z.output<typeof rowSchema>;

const generalSchema = z.strictObject({
  title: text(200),
  /** Faaliyet (`ra_activities`); şablondan dönüşen değerlendirmede `null`. */
  activityId: nullable(uuid),
  activityCode: optionalText(40),
  activityName: optionalText(200),
  assessmentType: z.enum(RA_ASSESSMENT_TYPES, { error: 'INVALID' }),
  /** Uygulanabilirlik (gemi tipi/grubu); dağıtım `ra_assignments`'tadır. */
  vesselGroups: uniqueList(z.enum(RA_VESSEL_GROUPS, { error: 'INVALID' }), 1, 5).refine(
    (list) => !list.includes('ALL') || list.length === 1,
    'GROUPS_ALL_EXCLUSIVE',
  ),
  location: optionalText(300),
  team: optionalText(500),
  procedures: optionalText(1000),
  preparedDay: nullable(localDate),
  reviewDay: nullable(localDate),
  scope: optionalText(2000),
  assumptions: optionalText(2000),
  exclusions: optionalText(2000),
});
export type RaLibraryGeneral = z.output<typeof generalSchema>;

/**
 * Faaliyete özel operasyon kontrol maddesi (K5; ADR 0089 §2; sahip kararı 2026-10-19, soru 4:
 * isteğe bağlı). Sürümle yayımlanır ve gemideki doğrulamanın checklist'ine altı ortak maddeden
 * sonra girer. N/A yalnız şirketin işaretlediği maddede ve gerekçeyle verilir (SS §5, BR-04).
 */
const checkSchema = z.strictObject({
  id,
  text: text(300),
  naAllowed: z.boolean({ error: 'INVALID' }),
});
export type RaLibraryCheck = z.output<typeof checkSchema>;
export const RA_LIBRARY_MAX_CHECKS = 30;

/** Değerlendirmenin içeriği (taslak düzeyi; eksik olabilir). Yayımlanan sürüm bunu taşır. */
export const raLibraryContentSchema = z
  .strictObject({
    general: generalSchema,
    rows: z.array(rowSchema).max(RA_LIBRARY_MAX_ROWS, 'TOO_MANY'),
    /**
     * Faaliyete özel maddeler (K5): isteğe bağlı; boş liste ile yokluk aynıdır. Anahtar yalnız
     * madde varsa yazılır: K3 sürümlerinin içeriği ve hash'i değişmez.
     */
    checks: z.array(checkSchema).max(RA_LIBRARY_MAX_CHECKS, 'TOO_MANY').optional(),
  })
  .refine((c) => {
    const ids = [
      ...c.rows.map((r) => r.id),
      ...c.rows.flatMap((r) => [...r.existingControls, ...r.additionalControls].map((x) => x.id)),
      ...(c.checks ?? []).map((x) => x.id),
    ];
    return new Set(ids).size === ids.length;
  }, 'DUPLICATE_ID');
export type RaLibraryContent = z.output<typeof raLibraryContentSchema>;

/** Faaliyete özel maddeler; anahtar yoksa boş. */
export function raLibraryChecks(content: Pick<RaLibraryContent, 'checks'>): RaLibraryCheck[] {
  return content.checks ?? [];
}

function toIssues(error: z.ZodError): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.message,
  }));
}

export function parseRaLibraryContent(input: unknown): ParseResult<RaLibraryContent> {
  const result = raLibraryContentSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Biçim hatası `INVALID_RA_LIBRARY` (ayrıntı alan:kod listesi). */
export function raLibraryContentOrThrow(input: unknown): RaLibraryContent {
  const parsed = parseRaLibraryContent(input);
  if (!parsed.ok) {
    throw new DomainError(
      'INVALID_RA_LIBRARY',
      parsed.issues.map((i) => `${i.path}:${i.code}`).join(','),
      parsed.issues[0]?.path,
    );
  }
  return parsed.value;
}

// ---------------------------------------------------------------------------------------------
// Kimlik ve revizyon

/** Değerlendirme kimliği (R8): `RA-<faaliyet kodu>`; revizyonlar boyunca sabit. */
export function raAssessmentCode(sourceCode: string): string {
  const code = `RA-${sourceCode.trim().toUpperCase()}`;
  if (!ASSESSMENT_CODE.test(code)) throw new DomainError('INVALID_CODE', code, 'code');
  return code;
}

/** Kimlik biçimi geçerli mi. */
export function isRaAssessmentCode(code: string): boolean {
  return ASSESSMENT_CODE.test(code);
}

/** Sonraki revizyon (R8): ilk yayım `1.0`, sonra `1.1`, `1.2`… */
export function raNextRevision(lastPublished: string | null): string {
  if (lastPublished === null) return '1.0';
  const match = REVISION.exec(lastPublished);
  if (!match) throw new DomainError('VALIDATION_FAILED', lastPublished, 'revision');
  return `${Number(match[1])}.${Number(match[2]) + 1}`;
}

/** Revizyonların sırası (`1.2` < `1.10`). */
export function compareRaRevision(a: string, b: string): number {
  const pa = REVISION.exec(a);
  const pb = REVISION.exec(b);
  if (!pa || !pb) return a.localeCompare(b);
  return Number(pa[1]) - Number(pb[1]) || Number(pa[2]) - Number(pb[2]);
}

// ---------------------------------------------------------------------------------------------
// İş akışı

const TRANSITIONS: Readonly<
  Record<RaLibraryEvent, { from: readonly RaLibraryStatus[]; to: RaLibraryStatus | null }>
> = {
  submitted: { from: ['draft', 'returned'], to: 'submitted' },
  review_started: { from: ['submitted'], to: 'under_review' },
  returned: { from: ['submitted', 'under_review', 'approved'], to: 'returned' },
  // Kabul durumu değiştirmez: incelemede yükseltilen riskin ofis kabulü
  accepted: { from: ['under_review'], to: null },
  approved: { from: ['under_review'], to: 'approved' },
  published: { from: ['approved'], to: 'published' },
};

/** Olayın sonraki durumu; geçersiz geçiş `INVALID_TRANSITION`. */
export function raLibraryNext(status: RaLibraryStatus, event: RaLibraryEvent): RaLibraryStatus {
  const rule = TRANSITIONS[event];
  if (!rule.from.includes(status)) {
    throw new DomainError('INVALID_TRANSITION', `${status} → ${event}`);
  }
  return rule.to ?? status;
}

/** Olayın geçerli olduğu durumlar (arayüzün düğmeleri). */
export function raLibraryEventAllowed(status: RaLibraryStatus, event: RaLibraryEvent): boolean {
  return TRANSITIONS[event].from.includes(status);
}

/** İçerik yalnız taslakta ve iadede düzenlenir; yayımlı değerlendirme yeni revizyon açar. */
export function raLibraryEditable(status: RaLibraryStatus): boolean {
  return status === 'draft' || status === 'returned';
}

// ---------------------------------------------------------------------------------------------
// Puanlar ve kabul

export interface RaRowRatings {
  inherent: RiskRating;
  current: RiskRating | null;
  /** Hedef kalan risk; girilmemişse `null`. */
  residual: RiskRating | null;
  /** Ek önlemlerin hepsi doğrulanmış mı: değilse hedef bir tahmindir (W-01, W-02). */
  residualVerified: boolean;
  /** Kabulün dayandığı puan: hedef, yoksa current, yoksa inherent. */
  decisive: RiskRating;
  acceptance: RaAcceptance;
  acceptedBy: readonly RaAcceptor[];
}

/** Satırın üç puanı ve kabul durumu (R7, R13). */
export function raRowRatings(row: RaLibraryRow, matrix: RaMatrixDefinition): RaRowRatings {
  const inherent = ratingIn(matrix, row.initial.likelihood, row.initial.severity);
  const current = row.current
    ? ratingIn(matrix, row.current.likelihood, row.current.severity)
    : null;
  const residual = row.residual
    ? ratingIn(matrix, row.residual.likelihood, row.residual.severity)
    : null;
  const decisive = residual ?? current ?? inherent;
  const band = bandOf(matrix, decisive.score);
  return {
    inherent,
    current,
    residual,
    residualVerified:
      residual !== null &&
      row.additionalControls.length > 0 &&
      row.additionalControls.every((c) => c.status === 'verified'),
    decisive,
    acceptance: band.acceptance,
    acceptedBy: band.acceptedBy,
  };
}

/** Değerlendirmenin en kötü kabul durumu ve en yüksek puanları (liste ve özet). */
export function raLibrarySummary(
  content: RaLibraryContent,
  matrix: RaMatrixDefinition,
): {
  inherent: RiskRating | null;
  residual: RiskRating | null;
  acceptance: RaAcceptance | null;
  rows: number;
  missingResidual: number;
} {
  const order = ['acceptable', 'controls_required', 'escalation', 'unacceptable'] as const;
  let inherent: RiskRating | null = null;
  let residual: RiskRating | null = null;
  let acceptance: RaAcceptance | null = null;
  let missingResidual = 0;
  for (const row of content.rows) {
    const r = raRowRatings(row, matrix);
    if (!inherent || r.inherent.score > inherent.score) inherent = r.inherent;
    if (r.residual && (!residual || r.residual.score > residual.score)) residual = r.residual;
    if (!r.residual) missingResidual += 1;
    if (acceptance === null || order.indexOf(r.acceptance) > order.indexOf(acceptance)) {
      acceptance = r.acceptance;
    }
  }
  return { inherent, residual, acceptance, rows: content.rows.length, missingResidual };
}

/**
 * Yayımdan önce ofiste kabul etmesi gereken roller (sahip kararı 2026-10-19, soru 3): kabul durumu
 * "Escalation Required" olan satırların bandındaki ofis rolleri. Kaptanın kabulü operasyonda (K5).
 */
export function raRequiredAcceptors(
  content: RaLibraryContent,
  matrix: RaMatrixDefinition,
): { office: RaAcceptor[]; vessel: RaAcceptor[] } {
  const office = new Set<RaAcceptor>();
  const vessel = new Set<RaAcceptor>();
  for (const row of content.rows) {
    const r = raRowRatings(row, matrix);
    if (r.acceptance !== 'escalation') continue;
    for (const role of r.acceptedBy) {
      if (RA_OFFICE_ACCEPTORS.includes(role)) office.add(role);
      else vessel.add(role);
    }
  }
  const sorted = (set: Set<RaAcceptor>) =>
    [...set].sort((a, b) => RA_ACCEPTOR_ORDER.indexOf(a) - RA_ACCEPTOR_ORDER.indexOf(b));
  return { office: sorted(office), vessel: sorted(vessel) };
}
const RA_ACCEPTOR_ORDER: readonly RaAcceptor[] = ['master', 'superintendent', 'dpa'];

// ---------------------------------------------------------------------------------------------
// Gönderim ve yayım kapısı

export type RaPublishIssueCode =
  | 'ROWS_REQUIRED'
  | 'PREPARED_DAY_REQUIRED'
  | 'REVIEW_DAY_REQUIRED'
  | 'REVIEW_BEFORE_PREPARED'
  | 'REVIEW_TOO_LATE'
  | 'HAZARD_REQUESTED'
  | 'CURRENT_ABOVE_INITIAL'
  | 'CURRENT_WITHOUT_CONTROLS'
  | 'ADDITIONAL_CONTROLS_REQUIRED'
  | 'ADDITIONAL_CONTROL_INCOMPLETE'
  | 'RESIDUAL_REQUIRED'
  | 'RESIDUAL_ABOVE_INITIAL'
  | 'RESIDUAL_REDUCTION_UNSUPPORTED'
  | 'SEVERITY_REDUCTION_UNSUPPORTED'
  | 'RESIDUAL_RATIONALE_REQUIRED'
  | 'RESIDUAL_UNACCEPTABLE';

export interface RaPublishIssue {
  code: RaPublishIssueCode;
  /** Satır ya da önlem kimliği; genel alanlarda `null`. */
  rowId: string | null;
  controlId?: string;
}

/** Önlem riski düşürür mü: taslak önlem düşürmez (Tab 5). */
const committed = (c: RaAdditionalControl) => c.status !== 'draft';
/** KKD şiddeti düşürmez (R7): şiddeti yalnız KKD dışı bir önlem düşürür. */
const reducesSeverity = (c: RaAdditionalControl) =>
  committed(c) &&
  (c.expectedEffect === 'severity' || c.expectedEffect === 'both') &&
  c.hierarchy.some((h) => h !== 'ppe');

/**
 * Gönderim ve yayım kapısı (FD §5–§7, §12; SS BR-06; XL W-01…W-03). Taslak eksik olabilir;
 * gönderilen ve yayımlanan olamaz. Sorunlar sırasıyla, satır kimliğiyle döner.
 */
export function raLibraryIssues(
  content: RaLibraryContent,
  matrix: RaMatrixDefinition,
  /** Gözden geçirmenin en geç ayı (şirket ayarı, ADR 0095 §3); en çok 12 (ADR 0050 §3). */
  options: { reviewMonths?: number | undefined } = {},
): RaPublishIssue[] {
  const reviewMonths = Math.min(options.reviewMonths ?? MAX_REVIEW_MONTHS, MAX_REVIEW_MONTHS);
  const issues: RaPublishIssue[] = [];
  const push = (code: RaPublishIssueCode, rowId: string | null = null, controlId?: string) =>
    issues.push(controlId ? { code, rowId, controlId } : { code, rowId });
  const g = content.general;
  if (g.preparedDay === null) push('PREPARED_DAY_REQUIRED');
  if (g.reviewDay === null) push('REVIEW_DAY_REQUIRED');
  if (g.preparedDay !== null && g.reviewDay !== null) {
    if (compareLocalDate(g.reviewDay, g.preparedDay) <= 0) push('REVIEW_BEFORE_PREPARED');
    else if (compareLocalDate(g.reviewDay, addMonths(g.preparedDay, reviewMonths)) > 0) {
      push('REVIEW_TOO_LATE');
    }
  }
  if (content.rows.length === 0) push('ROWS_REQUIRED');
  for (const row of content.rows) {
    const r = raRowRatings(row, matrix);
    if (row.hazardRequested) push('HAZARD_REQUESTED', row.id);
    // Current yalnız yerinde olduğu doğrulanmış mevcut kontrolle düşer (Tab 4)
    if (r.current) {
      if (r.current.score > r.inherent.score) push('CURRENT_ABOVE_INITIAL', row.id);
      else if (
        r.current.score < r.inherent.score &&
        !row.existingControls.some((c) => c.inPlace === 'yes')
      ) {
        push('CURRENT_WITHOUT_CONTROLS', row.id);
      }
    }
    const baseline = r.current ?? r.inherent;
    const baselineBand = bandOf(matrix, baseline.score);
    // Kabul eşiği aşılıyorsa ek önlem zorunlu (Tab 5); taslak önlem sayılmaz
    if (baselineBand.acceptance !== 'acceptable' && !row.additionalControls.some(committed)) {
      push('ADDITIONAL_CONTROLS_REQUIRED', row.id);
    }
    for (const c of row.additionalControls) {
      if (committed(c) && (c.expectedEffect === null || c.responsibleRole === null)) {
        push('ADDITIONAL_CONTROL_INCOMPLETE', row.id, c.id);
      }
    }
    const residual = row.residual;
    if (!residual || !r.residual) {
      push('RESIDUAL_REQUIRED', row.id);
      continue;
    }
    if (r.residual.score > baseline.score) push('RESIDUAL_ABOVE_INITIAL', row.id);
    else if (r.residual.score < baseline.score) {
      if (!row.additionalControls.some(committed)) {
        push('RESIDUAL_REDUCTION_UNSUPPORTED', row.id);
      } else if (
        residual.severity < baseline.severity &&
        !row.additionalControls.some(reducesSeverity)
      ) {
        push('SEVERITY_REDUCTION_UNSUPPORTED', row.id);
      }
      if (residual.rationale === null) push('RESIDUAL_RATIONALE_REQUIRED', row.id);
    }
    if (r.acceptance === 'unacceptable') push('RESIDUAL_UNACCEPTABLE', row.id);
  }
  return issues;
}

/** İlk sorun `DomainError` olur (kod, satır kimliği). */
export function assertRaLibraryPublishable(
  content: RaLibraryContent,
  matrix: RaMatrixDefinition,
  options: { reviewMonths?: number | undefined } = {},
): void {
  const [issue] = raLibraryIssues(content, matrix, options);
  if (issue) throw new DomainError(issue.code, issue.rowId ?? 'general', issue.controlId);
}

// ---------------------------------------------------------------------------------------------
// Sürüm içeriği

export interface RaVersionContentInput {
  assessmentId: string;
  code: string;
  revision: string;
  content: RaLibraryContent;
  /** Yayım anındaki matris sürümü (ADR 0085 §4); şablondan dönüşen sürümde `null`. */
  matrix: RaMatrixSnapshot | null;
  /** Şablondan dönüşen sürüm (K3 veri dönüşümü): kaynak şablon ve sürümü. */
  migratedFrom?: { templateId: string; templateVersion: number } | null;
}

/** Sürümün içerik nesnesi; `content_hash` = SHA-256(JCS(nesne)), hash'i `packages/db` hesaplar. */
export function raVersionContent(input: RaVersionContentInput) {
  if (!REVISION.test(input.revision)) {
    throw new DomainError('VALIDATION_FAILED', input.revision, 'revision');
  }
  return {
    assessmentId: input.assessmentId,
    code: input.code,
    revision: input.revision,
    general: { ...input.content.general },
    rows: input.content.rows.map((r) => ({ ...r })),
    // Faaliyete özel maddeler yalnız varsa (K5): K3 sürümlerinin hash'i aynı kalır
    ...(raLibraryChecks(input.content).length > 0
      ? { checks: raLibraryChecks(input.content).map((c) => ({ ...c })) }
      : {}),
    matrix: input.matrix
      ? {
          versionId: input.matrix.versionId,
          version: input.matrix.version,
          definition: input.matrix.definition,
        }
      : null,
    ...(input.migratedFrom ? { migratedFrom: { ...input.migratedFrom } } : {}),
  };
}
export type RaVersionContent = ReturnType<typeof raVersionContent>;

/** İki içerik aynı mı (yeni revizyon taslağında değişiklik var mı). */
export function sameRaLibraryContent(a: RaLibraryContent, b: RaLibraryContent): boolean {
  // Boş madde listesi ile anahtarın yokluğu aynı içeriktir
  const normal = (c: RaLibraryContent) => {
    const { checks, ...rest } = raLibraryContentSchema.parse(c);
    return JSON.stringify(checks && checks.length > 0 ? { ...rest, checks } : rest);
  };
  return normal(a) === normal(b);
}

// ---------------------------------------------------------------------------------------------
// Atama (R15)

export interface RaAssignmentTarget {
  /** Gemi grubu (`ALL` ya da `vessels.ra_group` değeri); belirli gemide `null`. */
  vesselGroup: string | null;
  /** Belirli gemi; grupta `null`. */
  vesselId: string | null;
}

/**
 * Değerlendirme gemiye atanmış mı (R15): `ALL`, geminin grubu ya da gemi kendisi. Atanmamış gemi
 * yayımlı RA'yı "güncel" diye kullanamaz.
 */
export function raAssignedTo(
  assignments: readonly RaAssignmentTarget[],
  vessel: { id: string; raGroup: RaShipGroup | string | null },
): boolean {
  return assignments.some(
    (a) =>
      a.vesselGroup === 'ALL' ||
      (a.vesselGroup !== null && a.vesselGroup === vessel.raGroup) ||
      (a.vesselId !== null && a.vesselId === vessel.id),
  );
}

/** Atamanın biçimi: ya grup ya gemi. */
export function assertRaAssignmentTarget(target: RaAssignmentTarget): void {
  const group = target.vesselGroup;
  if ((group === null) === (target.vesselId === null)) {
    throw new DomainError('ASSIGNMENT_TARGET', 'group xor vessel');
  }
  if (group !== null && !(RA_VESSEL_GROUPS as readonly string[]).includes(group)) {
    throw new DomainError('ASSIGNMENT_TARGET', group);
  }
}

// ---------------------------------------------------------------------------------------------
// Kaynaklardan içerik

/** Kütüphane bağının kontrol durumu → mevcut kontrol yerinde mi. */
function inPlaceOf(status: RaRowControlStatus): RaInPlace {
  if (status === 'implemented') return 'yes';
  if (status === 'not_implemented') return 'no';
  return 'unknown';
}

export interface RaMasterSources {
  activity: Pick<RaActivityData, 'code' | 'name' | 'scope' | 'steps' | 'vesselGroups'> & {
    id: string;
    assessmentTypes: readonly string[];
  };
  links: readonly (Pick<
    RaLinkData,
    | 'code'
    | 'stepId'
    | 'hazardId'
    | 'likelihood'
    | 'severity'
    | 'residualLikelihood'
    | 'residualSeverity'
    | 'existingControlIds'
    | 'additionalControlIds'
    | 'controlStatus'
    | 'evidence'
    | 'exposed'
    | 'status'
  > & { id: string })[];
  hazards: ReadonlyMap<string, { name: string; consequenceIds: readonly string[] }>;
  consequences: ReadonlyMap<string, string>;
  controls: ReadonlyMap<
    string,
    {
      measure: string;
      hierarchy: readonly RaControlHierarchy[];
      requirement: 'mandatory' | 'conditional' | null;
      evidence: string | null;
      responsibleRole: string | null;
    }
  >;
}

/**
 * Faaliyetin kütüphane bağlarından taslak şirket RA'sı (README §3.3, 05 + 06; XL W-02, W-03):
 * her etkin bağ bir satır. Mevcut kontroller bağın kontrol durumuyla gelir ("Not verified"
 * uygulandı sayılmaz); ek kontroller taslak öneridir (riski düşürmez). Kalan risk yalnız bağda
 * doğrulanmış olarak varsa gelir, yoksa boş kalır.
 */
export function raContentFromMaster(
  source: RaMasterSources,
  newId: () => string,
): RaLibraryContent {
  const a = source.activity;
  const steps = new Map(a.steps.map((s) => [s.id, s.text]));
  const rows = source.links
    .filter((l) => l.status !== 'retired')
    .flatMap((link): RaLibraryRow[] => {
      const hazard = source.hazards.get(link.hazardId);
      if (!hazard) return [];
      // Değerlendirilmemiş öneri (FINAL; ADR 0098 §4) şirket RA'sına varsayılanla girmez: önce
      // ana veride yetkin kişi ilk riski girer
      const { likelihood, severity } = link;
      if (likelihood === null || severity === null) {
        throw new DomainError('LINK_NOT_ASSESSED', link.code, 'likelihood');
      }
      const consequences = hazard.consequenceIds
        .map((cid) => source.consequences.get(cid))
        .filter((n): n is string => typeof n === 'string');
      const control = (cid: string) => source.controls.get(cid);
      const existing = link.existingControlIds.flatMap((cid): RaExistingControl[] => {
        const c = control(cid);
        return c
          ? [
              {
                id: newId(),
                controlId: cid,
                measure: c.measure,
                hierarchy: [...c.hierarchy],
                requirement: c.requirement,
                procedure: null,
                owner: c.responsibleRole,
                evidence: c.evidence ?? link.evidence,
                inPlace: inPlaceOf(link.controlStatus),
                effectiveness: null,
              },
            ]
          : [];
      });
      const additional = link.additionalControlIds.flatMap((cid): RaAdditionalControl[] => {
        const c = control(cid);
        return c
          ? [
              {
                id: newId(),
                controlId: cid,
                measure: c.measure,
                hierarchy: [...c.hierarchy],
                expectedEffect: null,
                effectRationale: null,
                responsibleRole: c.responsibleRole,
                dueDay: null,
                evidence: c.evidence,
                verifier: null,
                completionCriteria: null,
                status: 'draft',
              },
            ]
          : [];
      });
      const residual =
        link.residualLikelihood !== null &&
        link.residualSeverity !== null &&
        link.controlStatus !== 'not_verified'
          ? {
              likelihood: link.residualLikelihood,
              severity: link.residualSeverity,
              rationale: null,
              dependency: null,
            }
          : null;
      return [
        {
          id: newId(),
          linkId: link.id,
          stepId: link.stepId,
          step: (link.stepId ? steps.get(link.stepId) : undefined) ?? a.scope ?? a.name,
          hazardId: link.hazardId,
          hazard: hazard.name,
          hazardRequested: false,
          consequenceIds: [...hazard.consequenceIds],
          consequence: consequences.length > 0 ? consequences.join('; ') : null,
          exposed: link.exposed,
          initial: {
            likelihood,
            severity,
            rationale: null,
            assumptions: null,
          },
          current: null,
          existingControls: existing,
          additionalControls: additional,
          residual,
        },
      ];
    });
  const type = a.assessmentTypes.find((t) =>
    (RA_ASSESSMENT_TYPES as readonly string[]).includes(t),
  );
  return raLibraryContentOrThrow({
    general: {
      title: a.name,
      activityId: a.id,
      activityCode: a.code,
      activityName: a.name,
      assessmentType: type ?? 'routine',
      vesselGroups: [...a.vesselGroups],
      location: null,
      team: null,
      procedures: null,
      preparedDay: null,
      reviewDay: null,
      scope: a.scope,
      assumptions: null,
      exclusions: null,
    },
    rows,
  });
}

/** Şablonun önlem türü → kontrol hiyerarşisi (eğitim idari önlemdir). */
function hierarchyOfType(type: ControlType): RaControlHierarchy[] {
  return [type === 'training' ? 'administrative' : type];
}

/**
 * Şirket şablonundan "Published 1.0 (migrated)" içeriği (K3 veri dönüşümü; ADR 0050 §5):
 * tehlike satır olur, ilk ve kalan risk aynen kalır. Şablonun önlemleri ilk riskten kalan riske
 * götüren önlemlerdir (ADR 0050 §2): ek önlem olarak `planned` gelir. Tehlikeye bağlı olmayan
 * genel önlem her satıra eklenir. Kayıt kaybolmaz; uydurma alan yazılmaz (beklenen etki boş).
 */
export function raContentFromTemplate(
  template: Pick<RiskTemplateContent, 'title' | 'activity' | 'hazards' | 'controls'>,
  input: { reviewDay: string | null; preparedDay: string | null },
  newId: () => string,
): RaLibraryContent {
  const step = template.activity.trim() || template.title;
  return raLibraryContentOrThrow({
    general: {
      title: template.title,
      activityId: null,
      activityCode: null,
      activityName: null,
      assessmentType: 'routine',
      vesselGroups: ['ALL'],
      location: null,
      team: null,
      procedures: null,
      preparedDay: input.preparedDay,
      reviewDay: input.reviewDay,
      scope: template.activity.trim() || null,
      assumptions: null,
      exclusions: null,
    },
    rows: template.hazards.map((h) => ({
      id: newId(),
      linkId: null,
      stepId: null,
      step: step.slice(0, 300),
      hazardId: null,
      hazard: h.hazard,
      hazardRequested: false,
      consequenceIds: [],
      consequence: h.consequence || null,
      exposed: null,
      initial: {
        likelihood: h.likelihood,
        severity: h.severity,
        rationale: null,
        assumptions: null,
      },
      current: null,
      existingControls: [],
      additionalControls: template.controls
        .filter((c) => c.hazardId === h.id || c.hazardId === null)
        .map((c) => ({
          id: newId(),
          controlId: null,
          measure: c.measure,
          hierarchy: hierarchyOfType(c.type),
          expectedEffect: null,
          effectRationale: null,
          responsibleRole: c.responsible,
          dueDay: null,
          evidence: null,
          verifier: null,
          completionCriteria: null,
          status: 'planned',
        })),
      residual: {
        likelihood: h.residualLikelihood,
        severity: h.residualSeverity,
        rationale: null,
        dependency: null,
      },
    })),
  });
}

/** Gemi RA'sının önlem türü: birincil hiyerarşi, yoksa idari. */
function typeOfHierarchy(list: readonly RaControlHierarchy[]): ControlType {
  return list[0] ?? 'administrative';
}

/**
 * Yayımlı sürümden gemiye özel RA ("Vessel Task-Specific RA"; soru 8): satır tehlike olur, ilk
 * risk current varsa onunla, yoksa inherent ile gelir. **Kalan risk ilk riske eşit başlar**: gemi
 * önlemlerin yerinde olduğunu doğrulayıp düşürür (ADR 0086 §7, XL W-02). Mevcut kontrol `open`,
 * ek kontrol `planned` gelir; taslak öneri gelmez. Sorumlu boşsa "Master".
 */
export function shipRiskFromVersion(
  content: Pick<RaLibraryContent, 'general' | 'rows'>,
  newId: () => string,
): Pick<RiskAssessmentContent, 'hazards' | 'controls'> & { title: string; activity: string } {
  const hazards: RiskHazard[] = [];
  const controls: RiskControl[] = [];
  for (const row of content.rows) {
    const hazardId = newId();
    const base = row.current ?? row.initial;
    hazards.push({
      id: hazardId,
      hazard: row.hazard,
      consequence: (row.consequence ?? '').slice(0, 500),
      likelihood: base.likelihood,
      severity: base.severity,
      residualLikelihood: base.likelihood,
      residualSeverity: base.severity,
    });
    const responsible = (value: string | null) => (value ?? 'Master').slice(0, 120);
    for (const c of row.existingControls) {
      controls.push({
        id: newId(),
        hazardId,
        type: typeOfHierarchy(c.hierarchy),
        measure: c.measure.slice(0, 300),
        responsible: responsible(c.owner),
        dueDay: null,
        status: 'open',
      });
    }
    for (const c of row.additionalControls) {
      if (c.status === 'draft') continue;
      controls.push({
        id: newId(),
        hazardId,
        type: typeOfHierarchy(c.hierarchy),
        measure: c.measure.slice(0, 300),
        responsible: responsible(c.responsibleRole),
        dueDay: null,
        status: 'planned',
      });
    }
  }
  const g = content.general;
  return {
    title: g.title,
    activity: (g.scope ?? g.activityName ?? g.title).slice(0, 2000),
    hazards,
    controls,
  };
}
